import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHeader } from '@/components/PageHeader';
import { toast } from 'sonner';
import { Download, Upload, Database, Clock, CheckCircle, XCircle, Loader2, Trash2, AlertCircle, Shield, RefreshCw, Eye, RotateCcw, FileWarning, History, Play, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BACKUP_STATUS = {
  SCHEDULED: { label: 'Programmato', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  IN_PROGRESS: { label: 'In corso', color: 'bg-blue-100 text-blue-800', icon: Loader2 },
  COMPLETED: { label: 'Completato', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  FAILED: { label: 'Errore', color: 'bg-red-100 text-red-800', icon: XCircle }
};

const RESTORE_STATUS = {
  PENDING: { label: 'In attesa', color: 'bg-gray-100 text-gray-800' },
  VALIDATING: { label: 'Validazione', color: 'bg-yellow-100 text-yellow-800' },
  PREVIEWING: { label: 'Preview', color: 'bg-blue-100 text-blue-800' },
  IN_PROGRESS: { label: 'In corso', color: 'bg-orange-100 text-orange-800' },
  COMPLETED: { label: 'Completato', color: 'bg-green-100 text-green-800' },
  FAILED: { label: 'Errore', color: 'bg-red-100 text-red-800' },
  ROLLED_BACK: { label: 'Annullato', color: 'bg-purple-100 text-purple-800' }
};

export const Backups = () => {
  const { token, selectedCondominium } = useAuth();
  const userRole = selectedCondominium?.role || 'RESIDENT';
  const condoId = selectedCondominium?.condominium?.id;

  // State
  const [backups, setBackups] = useState([]);
  const [restores, setRestores] = useState([]);
  const [undoOps, setUndoOps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('backups');

  // Dialog states
  const [isCreateBackupOpen, setIsCreateBackupOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [selectedRestore, setSelectedRestore] = useState(null);

  // Form states
  const [backupType, setBackupType] = useState('FULL');
  const [uploadFile, setUploadFile] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Fetch functions
  const fetchBackups = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/backups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setBackups(await res.json());
    } catch (error) {
      console.error('Error fetching backups:', error);
    }
  }, [condoId, token]);

  const fetchRestores = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/restores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setRestores(await res.json());
    } catch (error) {
      console.error('Error fetching restores:', error);
    }
  }, [condoId, token]);

  const fetchUndoOps = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/undo-operations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setUndoOps(await res.json());
    } catch (error) {
      console.error('Error fetching undo operations:', error);
    }
  }, [condoId, token]);

  useEffect(() => {
    Promise.all([fetchBackups(), fetchRestores(), fetchUndoOps()]).then(() => setLoading(false));
    const interval = setInterval(() => {
      fetchBackups();
      fetchRestores();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchBackups, fetchRestores, fetchUndoOps]);

  // Create backup
  const handleCreateBackup = async () => {
    setProcessing(true);
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/backups?backup_type=${backupType}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (res.ok) {
        toast.success('Backup avviato');
        setIsCreateBackupOpen(false);
        fetchBackups();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore creazione backup');
    }
    setProcessing(false);
  };

  // Download backup
  const handleDownload = async (backupId) => {
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/backups/${backupId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `backup_${backupId.slice(0, 8)}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Download avviato');
      } else {
        toast.error('Errore download');
      }
    } catch (error) {
      toast.error('Errore download');
    }
  };

  // Verify backup chain
  const handleVerify = async (backupId) => {
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/backups/${backupId}/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const result = await res.json();
        if (result.valid) {
          toast.success(`Backup valido - Catena: ${result.chain_length} elementi`);
        } else {
          toast.error(result.error || 'Backup non valido');
        }
      }
    } catch (error) {
      toast.error('Errore verifica');
    }
  };

  // Delete backup
  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm('Eliminare questo backup?')) return;

    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/backups/${backupId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Backup eliminato');
        fetchBackups();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  // Upload backup for restore
  const handleUpload = async () => {
    if (!uploadFile) return;

    setProcessing(true);
    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/restores/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const result = await res.json();
        toast.success(result.message);
        setIsUploadOpen(false);
        setUploadFile(null);
        fetchRestores();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore upload');
    }
    setProcessing(false);
  };

  // Start restore from existing backup
  const handleStartRestore = async (backupId) => {
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/restores/from-backup/${backupId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const result = await res.json();
        toast.success(result.message);
        fetchRestores();
        setActiveTab('restores');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Preview restore
  const handlePreview = async (restoreId) => {
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/restores/${restoreId}/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const result = await res.json();
        setPreviewData(result);
        setSelectedRestore(restoreId);
        setIsPreviewOpen(true);
        fetchRestores();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore preview');
    }
    setProcessing(false);
  };

  // Execute restore
  const handleExecuteRestore = async () => {
    if (!selectedRestore) return;

    if (!window.confirm('ATTENZIONE: Il ripristino sovrascriverà i dati attuali. Continuare?')) return;

    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/restores/${selectedRestore}/execute`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const result = await res.json();
        toast.success(result.message);
        setIsPreviewOpen(false);
        fetchRestores();
        fetchUndoOps();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore ripristino');
    }
    setProcessing(false);
  };

  // Undo operation
  const handleUndo = async (undoId) => {
    if (!window.confirm('Annullare questa operazione e ripristinare lo stato precedente?')) return;

    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/undo/${undoId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Operazione annullata');
        fetchUndoOps();
        fetchRestores();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore undo');
    }
  };

  // Helpers
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Access check
  if (userRole !== 'ADMIN') {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
              <h2 className="text-xl font-semibold mb-2">Accesso Negato</h2>
              <p className="text-muted-foreground">
                Solo l'amministratore può gestire backup e ripristini.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Backup"
        subtitle="Backup e ripristino dei dati"
        actions={
          <>
          <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Upload className="mr-2 h-4 w-4" />
                Carica Backup
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Carica Backup per Ripristino</DialogTitle>
                <DialogDescription>
                  Carica un file di backup esistente per il ripristino
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                  <Database className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                  <Input
                    type="file"
                    accept=".zip"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    className="max-w-xs mx-auto"
                  />
                  <p className="text-sm text-muted-foreground mt-2">
                    Solo file ZIP di backup
                  </p>
                </div>

                <div className="bg-yellow-50 p-3 rounded-lg text-sm">
                  <p className="font-medium text-yellow-800 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Attenzione
                  </p>
                  <p className="text-yellow-700 mt-1">
                    Verrà creato uno snapshot prima del ripristino per permettere l'undo.
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsUploadOpen(false)}>
                  Annulla
                </Button>
                <Button onClick={handleUpload} disabled={!uploadFile || processing}>
                  {processing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  Carica
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={isCreateBackupOpen} onOpenChange={setIsCreateBackupOpen}>
            <DialogTrigger asChild>
              <Button>
                <Database className="mr-2 h-4 w-4" />
                Nuovo Backup
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Crea Nuovo Backup</DialogTitle>
                <DialogDescription>
                  Scegli il tipo di backup da creare
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Tipo Backup</Label>
                  <Select value={backupType} onValueChange={setBackupType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FULL">
                        <div>
                          <div className="font-medium">Backup Completo (FULL)</div>
                          <div className="text-xs text-muted-foreground">Tutti i dati e documenti</div>
                        </div>
                      </SelectItem>
                      <SelectItem value="DELTA">
                        <div>
                          <div className="font-medium">Backup Incrementale (DELTA)</div>
                          <div className="text-xs text-muted-foreground">Solo modifiche dall'ultimo FULL</div>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-blue-50 p-3 rounded-lg text-sm">
                  <p className="text-blue-800">
                    {backupType === 'FULL'
                      ? 'Il backup completo include tutti i dati e documenti del condominio.'
                      : 'Il backup incrementale richiede un backup FULL esistente e include solo le modifiche.'}
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateBackupOpen(false)}>
                  Annulla
                </Button>
                <Button onClick={handleCreateBackup} disabled={processing}>
                  {processing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Database className="mr-2 h-4 w-4" />}
                  Crea Backup
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </>
        }
      />

      {/* Undo Alert */}
      {undoOps.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <RotateCcw className="h-5 w-5 text-orange-600" />
                <div>
                  <p className="font-medium text-orange-800">Operazioni annullabili disponibili</p>
                  <p className="text-sm text-orange-700">
                    Puoi annullare l'ultimo {undoOps[0].operation_type.toLowerCase()} effettuato
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => handleUndo(undoOps[0].id)}>
                <RotateCcw className="mr-2 h-4 w-4" />
                Annulla
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="backups">
            <Database className="mr-2 h-4 w-4" />
            Backup
          </TabsTrigger>
          <TabsTrigger value="restores">
            <History className="mr-2 h-4 w-4" />
            Ripristini
          </TabsTrigger>
        </TabsList>

        {/* Backups Tab */}
        <TabsContent value="backups" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Storico Backup</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
              ) : backups.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun backup creato</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>Record</TableHead>
                      <TableHead>Documenti</TableHead>
                      <TableHead>Dimensione</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {backups.map((backup) => {
                      const statusInfo = BACKUP_STATUS[backup.status] || BACKUP_STATUS.SCHEDULED;
                      const StatusIcon = statusInfo.icon;
                      return (
                        <TableRow key={backup.id}>
                          <TableCell>
                            <Badge variant={backup.backup_type === 'FULL' ? 'default' : 'outline'}>
                              {backup.backup_type}
                            </Badge>
                            {backup.parent_backup_id && (
                              <span className="text-xs text-muted-foreground ml-1">
                                → {backup.parent_backup_id.slice(0, 6)}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusInfo.color}>
                              <StatusIcon className={`h-3 w-3 mr-1 ${backup.status === 'IN_PROGRESS' ? 'animate-spin' : ''}`} />
                              {statusInfo.label}
                            </Badge>
                          </TableCell>
                          <TableCell>{backup.record_count.toLocaleString()}</TableCell>
                          <TableCell>{backup.document_count}</TableCell>
                          <TableCell>{formatSize(backup.file_size)}</TableCell>
                          <TableCell>{formatDate(backup.created_at)}</TableCell>
                          <TableCell className="text-right space-x-1">
                            {backup.status === 'COMPLETED' && (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => handleVerify(backup.id)} title="Verifica">
                                  <Shield className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDownload(backup.id)} title="Scarica">
                                  <Download className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleStartRestore(backup.id)} title="Ripristina">
                                  <RefreshCw className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteBackup(backup.id)}>
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Restores Tab */}
        <TabsContent value="restores" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Operazioni di Ripristino</CardTitle>
            </CardHeader>
            <CardContent>
              {restores.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessuna operazione di ripristino</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>Record</TableHead>
                      <TableHead>Documenti</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {restores.map((restore) => {
                      const statusInfo = RESTORE_STATUS[restore.status] || RESTORE_STATUS.PENDING;
                      return (
                        <TableRow key={restore.id}>
                          <TableCell className="font-mono text-sm">
                            {restore.id.slice(0, 8)}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusInfo.color}>
                              {statusInfo.label}
                            </Badge>
                          </TableCell>
                          <TableCell>{restore.records_restored.toLocaleString()}</TableCell>
                          <TableCell>{restore.documents_restored}</TableCell>
                          <TableCell>{formatDate(restore.created_at)}</TableCell>
                          <TableCell className="text-right space-x-1">
                            {['PENDING', 'PREVIEWING'].includes(restore.status) && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handlePreview(restore.id)}
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  Preview
                                </Button>
                              </>
                            )}
                            {restore.status === 'COMPLETED' && restore.snapshot_id && (
                              <Badge variant="outline" className="text-green-600">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Snapshot salvato
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview Ripristino</DialogTitle>
            <DialogDescription>
              Riepilogo delle modifiche che verranno applicate
            </DialogDescription>
          </DialogHeader>

          {previewData && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-green-600">
                      +{previewData.diff?.summary?.total_additions || 0}
                    </div>
                    <div className="text-sm text-muted-foreground">Aggiunte</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {previewData.diff?.summary?.total_updates || 0}
                    </div>
                    <div className="text-sm text-muted-foreground">Aggiornamenti</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-red-600">
                      -{previewData.diff?.summary?.total_deletions || 0}
                    </div>
                    <div className="text-sm text-muted-foreground">Eliminazioni</div>
                  </CardContent>
                </Card>
              </div>

              {/* Collections detail */}
              <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                {Object.entries(previewData.diff?.collections || {}).map(([name, data]) => (
                  <div key={name} className="p-3 flex justify-between items-center">
                    <span className="font-medium capitalize">{name.replace('_', ' ')}</span>
                    <div className="flex gap-4 text-sm">
                      <span>Backup: {data.backup_count}</span>
                      <ChevronRight className="h-4 w-4" />
                      <span>Attuale: {data.current_count}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Warning */}
              <div className="bg-red-50 p-4 rounded-lg">
                <p className="font-medium text-red-800 flex items-center gap-2">
                  <FileWarning className="h-5 w-5" />
                  Attenzione
                </p>
                <p className="text-sm text-red-700 mt-1">
                  Il ripristino sovrascriverà i dati attuali. Verrà creato uno snapshot automatico
                  che permetterà di annullare l'operazione se necessario.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleExecuteRestore} disabled={processing}>
              {processing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
              Esegui Ripristino
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};